
import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SuspendedAccountScreen from '@/components/auth/SuspendedAccountScreen';
import DeactivatedAccountScreen from '@/components/auth/DeactivatedAccountScreen';

import { authStateCoordinator } from '@/utils/authStateCoordinator';

const LoadingSpinner = () => (
  <div className="p-8 flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
      <span>Loading...</span>
    </div>
  </div>
);

export const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { session, isLoading, user, profile, isSuperuser, isAdmin } = useAuth();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const location = useLocation();
  
  console.log('[ProtectedRoute] Auth check for path:', location.pathname, {
    session: !!session,
    isLoading,
    hasCheckedAuth,
    profileStatus: profile?.status,
    companyId: profile?.company_id,
    emailConfirmed: user?.email_confirmed_at,
    profileCompleted: profile?.profile_completed
  });
  
  useEffect(() => {
    if (!isLoading) {
      setHasCheckedAuth(true);
    }
  }, [isLoading]);

  // Phase 2: Respect auth transitions in progress
  if (authStateCoordinator.shouldBlockNavigation()) {
    console.log('[ProtectedRoute] Navigation blocked due to active signout');
    return <LoadingSpinner />;
  }
  
  if (!hasCheckedAuth) {
    return <LoadingSpinner />;
  }
  
  if (!session) {
    console.log('[ProtectedRoute] No session, redirecting to login');
    return <Navigate to="/login" />;
  }

  // Wait for profile to load before rendering any protected content
  if (!profile) {
    return <LoadingSpinner />;
  }

  // Check if email is not confirmed - redirect to verify-email page
  if (user && !user.email_confirmed_at) {
    console.log('[ProtectedRoute] Email not confirmed, redirecting to verify-email');
    return <Navigate to="/verify-email" replace />;
  }

  // Check if user is suspended
  if (profile?.status === 'suspended' && !user?.impersonated) {
    console.log('[ProtectedRoute] User is suspended, showing suspension screen');

    // Already on suspended path — render it without redirecting
    if (location.pathname === '/suspended') {
      return children;
    }

    // Already on company-suspended path — render it
    if (location.pathname.startsWith('/company-suspended')) {
      return children;
    }

    // Any suspended user with a company — route to company suspended screen
    // CompanySuspendedScreen handles the case where the company isn't
    // actually suspended by redirecting to /suspended internally
    if (profile?.company_id) {
      return <Navigate
        to={`/company-suspended/${profile.company_id}`}
        replace
      />;
    }

    // Regular user — show standard suspended screen
    return (
      <SuspendedAccountScreen
        suspendedUntil={profile.suspended_until}
        suspensionReason={profile.suspension_reason}
      />
    );
  }

  // Allow access to deactivated and company-creation pages for deactivated users
  if (location.pathname === '/deactivated' || location.pathname === '/company-creation') {
    console.log('[ProtectedRoute] User accessing deactivated or company creation page');
    return children;
  }

  // Check if user is deactivated (after allowing access to specific pages)
  if (profile?.status === 'deactivated' && !user?.impersonated) {
    console.log('[ProtectedRoute] User is deactivated, redirecting to deactivated page');
    return <Navigate to="/deactivated" replace />;
  }

  // Allow access to profile setup if profile is not completed (even without company_id)
  if (profile && !profile.profile_completed && location.pathname === '/profile-setup') {
    console.log('[ProtectedRoute] User accessing profile setup with incomplete profile');
    return children;
  }

  // Redirect to profile setup if profile is not completed
  if (profile && !profile.profile_completed) {
    console.log('[ProtectedRoute] Profile not completed, redirecting to profile setup');
    return <Navigate to="/profile-setup" replace />;
  }

  // Superusers can access any protected route without needing a company_id
  if (isSuperuser) return children;

  // Phase 3: Enhanced corruption state handling - only after profile is completed
  if (profile && profile.profile_completed && !profile.company_id && location.pathname !== '/account/no-company' && location.pathname !== '/company-creation') {
    console.log('[ProtectedRoute] User has no company, redirecting to no-company page');
    return <Navigate to="/account/no-company" replace />;
  }
  
  return children;
};

export const AdminRoute = ({ children }: { children: JSX.Element }) => {
  const { session, isLoading, isAdmin, isSuperuser, user, profile } = useAuth();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  
  useEffect(() => {
    if (!isLoading) {
      setHasCheckedAuth(true);
    }
  }, [isLoading]);
  
  if (!hasCheckedAuth) {
    return <LoadingSpinner />;
  }
  
  if (!session) {
    return <Navigate to="/login" />;
  }

  // Check if user is suspended
  if (profile?.status === 'suspended') {
    if (profile?.company_id) {
      return <Navigate to={`/company-suspended/${profile.company_id}`} replace />;
    }
    return (
      <SuspendedAccountScreen 
        suspendedUntil={profile.suspended_until}
        suspensionReason={profile.suspension_reason}
      />
    );
  }

  // Check if user is deactivated
  if (profile?.status === 'deactivated') {
    return <Navigate to="/deactivated" replace />;
  }
  
  // Block access to admin routes when being impersonated
  if (user?.impersonated) {
    return <Navigate to="/dashboard" />;
  }
  
  if (!(isAdmin || isSuperuser)) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

export const UnauthenticatedOnlyRoute = ({ children }: { children: JSX.Element }) => {
  const { session, isLoading, profile, user, isSuperuser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  
  console.log('[UnauthenticatedOnlyRoute] Auth check for path:', location.pathname, {
    session: !!session,
    isLoading,
    hasCheckedAuth,
    profileCompleted: profile?.profile_completed,
    profileStatus: profile?.status,
    emailConfirmed: user?.email_confirmed_at
  });
  
  useEffect(() => {
    if (!isLoading) {
      setHasCheckedAuth(true);
    }
  }, [isLoading]);
  
  useEffect(() => {
    if (hasCheckedAuth && session) {
      console.log('[UnauthenticatedOnlyRoute] User authenticated, redirecting...');
      
      // First check if email is confirmed
      if (!user?.email_confirmed_at) {
        console.log('[UnauthenticatedOnlyRoute] Email not confirmed, redirecting to verify-email');
        navigate('/verify-email', { replace: true });
        return;
      }
      
      const status = (profile as any)?.status as string | undefined;
      // If suspended, send to dedicated suspended route
      if (status === 'suspended') {
        navigate('/suspended', { replace: true });
        return;
      }
      // If deactivated, send to dedicated deactivated route
      if (status === 'deactivated') {
        navigate('/deactivated', { replace: true });
        return;
      }
      if (profile?.profile_completed) {
        const destination = isSuperuser ? '/admin' : '/dashboard';
        navigate(destination, { replace: true });
      } else {
        navigate('/profile-setup', { replace: true });
      }
    }
  }, [hasCheckedAuth, session, (profile as any)?.status, profile?.profile_completed, user?.email_confirmed_at, navigate]);
  
  if (!hasCheckedAuth) {
    return <LoadingSpinner />;
  }
  
  if (session) {
    // Return loading spinner while redirect happens
    return <LoadingSpinner />;
  }
  
  return children;
};
